import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../config';
import './databases.css';

function TenantsCard() {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [tenants, setTenants] = useState([]);
  const [properties, setProperties] = useState([]);
  const [tenantAttributes, setTenantAttributes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tenantSearch, setTenantSearch] = useState('');
  const [tenantSortKey, setTenantSortKey] = useState('');
  const [tenantSortOrder, setTenantSortOrder] = useState('asc');
  const [showTenantModal, setShowTenantModal] = useState(false);
  const [showAddTenantAttributeModal, setShowAddTenantAttributeModal] = useState(false);
  const [showDeleteTenantAttributeModal, setShowDeleteTenantAttributeModal] = useState(false);
  const [newAttribute, setNewAttribute] = useState('');
  const [newAttributeType, setNewAttributeType] = useState('string');
  const [newAttributeRelationTable, setNewAttributeRelationTable] = useState('');
  const [attributeToDelete, setAttributeToDelete] = useState('');
  const [editTenant, setEditTenant] = useState(null);
  const [newTenant, setNewTenant] = useState({});
  const [attributeTypes, setAttributeTypes] = useState({});
  const [availableDatabases, setAvailableDatabases] = useState([]);
  const [relationOptions, setRelationOptions] = useState({});
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Column management state
  const [hiddenColumns, setHiddenColumns] = useState({});
  const [columnWidths, setColumnWidths] = useState({});
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeColumn, setResizeColumn] = useState(null);
  const [resizeStartX, setResizeStartX] = useState(0);
  const [resizeStartWidth, setResizeStartWidth] = useState(0);
  const [showExcelUploadModal, setShowExcelUploadModal] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [legalStatuses, setLegalStatuses] = useState({});
  const [complaintCounts, setComplaintCounts] = useState({});

  const MAX_VISIBLE_COLUMNS = 10;

  const toDbField = (displayName) => displayName.toLowerCase().replace(/\s+/g, '_');
  const toDisplayName = (dbField) => dbField.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');

  // Get property name for display
  const getPropertyName = (propertyId) => {
    if (!propertyId) return 'No Property Assigned';
    
    const property = properties.find(p => p.id === propertyId);
    if (!property) return 'Unknown Property';
    
    return property['Property Name'] || 
           property.property_name || 
           property.name || 
           property.Property || 
           property.propertyName || 
           `Property ID: ${propertyId}`;
  };

  // Get property ID from property name (for form handling)
  const getPropertyIdFromName = (propertyName) => {
    if (!propertyName) return null;
    
    const property = properties.find(p => 
      p['Property Name'] === propertyName ||
      p.property_name === propertyName ||
      p.name === propertyName ||
      p.Property === propertyName
    );
    
    return property ? property.id : null;
  };

  // Currency formatting
  const formatCurrency = (value) => {
    if (isNaN(Number(value))) return value;
    const n = Number(value);
    const formatted = n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
    return n < 0 ? `(${formatted.replace('-', '')})` : formatted;
  };

  const isCurrencyField = (fieldName) => {
    return attributeTypes[fieldName] === 'currency';
  };

  const isNumberField = (fieldName) => {
    return attributeTypes[fieldName] === 'number' || attributeTypes[fieldName] === 'currency';
  };

  // Legal status helper functions
  const getLegalStatusColor = (status) => {
    switch (status) {
      case 'good_standing': return '#28a745';
      case 'light_offender': return '#ffc107';
      case 'heavy_offender': return '#dc3545';
      case 'on_notice': return '#fd7e14';
      case 'eviction': return '#6f42c1';
      default: return '#6c757d';
    }
  };

  const getLegalStatusLabel = (status) => {
    switch (status) {
      case 'good_standing': return 'Good Standing';
      case 'light_offender': return 'Light Offender';
      case 'heavy_offender': return 'Heavy Offender';
      case 'on_notice': return 'On Notice';
      case 'eviction': return 'Eviction';
      default: return 'Unknown';
    }
  };

  const getLegalStatusDisplay = (tenantId) => {
    const legalStatus = legalStatuses[tenantId];
    if (!legalStatus || !legalStatus.status) {
      return {
        label: 'Good Standing',
        color: '#28a745'
      };
    }
    return {
      label: getLegalStatusLabel(legalStatus.status),
      color: getLegalStatusColor(legalStatus.status)
    };
  };

  // Column management functions
  const toggleColumnVisibility = (columnName) => {
    setHiddenColumns(prev => ({
      ...prev,
      [columnName]: !prev[columnName]
    }));
  };

  const resetColumnWidths = () => {
    setColumnWidths({});
  };

  const handleColumnResize = (columnName, newWidth) => {
    setColumnWidths(prev => ({
      ...prev,
      [columnName]: Math.max(100, newWidth)
    }));
  };

  const getColumnWidth = (columnName) => {
    return columnWidths[columnName] || 150;
  };

  const handleMouseDown = (e, columnName) => {
    e.preventDefault();
    setIsResizing(true);
    setResizeColumn(columnName);
    setResizeStartX(e.clientX);
    setResizeStartWidth(getColumnWidth(columnName));
  };

  const handleMouseMove = (e) => {
    if (!isResizing || !resizeColumn) return;
    
    const deltaX = e.clientX - resizeStartX;
    const newWidth = resizeStartWidth + deltaX;
    handleColumnResize(resizeColumn, newWidth);
  };

  const handleMouseUp = () => {
    setIsResizing(false);
    setResizeColumn(null);
  };

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isResizing, resizeColumn, resizeStartX, resizeStartWidth]);

  // Fetch attribute types
  const fetchAttributeTypes = async (tableName) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/${tableName}/attribute-types`);
      if (response.ok) {
        const data = await response.json();
        const types = data.attribute_types || {};
        setAttributeTypes(types);
        Object.entries(types).forEach(([, typeVal]) => {
          if (typeof typeVal === 'string' && typeVal.startsWith('relation:')) fetchRelationOptions(typeVal.split(':')[1]);
        });
      }
    } catch (error) {
      console.error('Error fetching attribute types:', error);
    }
  };

  const fetchAvailableDatabases = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/available-databases`);
      if (!res.ok) return;
      const data = await res.json();
      setAvailableDatabases(data.databases || []);
    } catch (e) { console.error('Error fetching available databases:', e); }
  };

  const fetchRelationOptions = async (tableName) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/${tableName}/relation-options`);
      if (!res.ok) return;
      const data = await res.json();
      setRelationOptions(prev => ({ ...prev, [tableName]: data.options || [] }));
    } catch (e) { console.error('Error fetching relation options:', e); }
  };

  useEffect(() => {
    const storedUser = localStorage.getItem('username');
    if (!storedUser) navigate('/');
    else setUsername(storedUser);
    fetchData();
    fetchAttributeTypes('tenants');
    fetchAvailableDatabases();
  }, [navigate]);

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      console.log('Fetching data from:', `${API_BASE_URL}/api/tenants`);
      const [tenantRes, propertyRes, attrRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/tenants`),
        fetch(`${API_BASE_URL}/api/properties`),
        fetch(`${API_BASE_URL}/api/tenants/attributes`)
      ]);
      
      console.log('Response statuses:', {
        tenants: tenantRes.status,
        properties: propertyRes.status,
        attributes: attrRes.status
      });
      
      if (!tenantRes.ok || !propertyRes.ok || !attrRes.ok) {
        console.error('One or more API calls failed:', {
          tenants: tenantRes.status,
          properties: propertyRes.status,
          attributes: attrRes.status
        });
        throw new Error('Failed to fetch data');
      }

      const [tenantData, propertyData, attrData] = await Promise.all([
        tenantRes.json(),
        propertyRes.json(),
        attrRes.json()
      ]);

      console.log('Fetched data:', {
        tenants: tenantData.length,
        properties: propertyData.length,
        attributes: attrData
      });

      setTenants(tenantData);
      setProperties(propertyData);

      const attrs = attrData.attributes || attrData;
      setTenantAttributes(attrs);

      const emptyNew = {};
      attrs.forEach(attr => {
        emptyNew[attr] = '';
      });
      emptyNew.property_id = '';
      setNewTenant(emptyNew);

      // Fetch legal statuses and complaint counts
      await fetchLegalStatusesAndComplaints(tenantData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchLegalStatusesAndComplaints = async (tenantsData) => {
    const legalStatusesData = {};
    const complaintCountsData = {};

    // Fetch legal status and complaint count for each tenant
    for (const tenant of tenantsData) {
      try {
        // Fetch legal status
        const legalStatusResponse = await fetch(`${API_BASE_URL}/api/tenants/${tenant.id}/legal-status`);
        if (legalStatusResponse.ok) {
          const legalStatus = await legalStatusResponse.json();
          legalStatusesData[tenant.id] = legalStatus;
        }

        // Fetch complaint count
        const complaintsResponse = await fetch(`${API_BASE_URL}/api/tenants/${tenant.id}/complaints`);
        if (complaintsResponse.ok) {
          const complaints = await complaintsResponse.json();
          complaintCountsData[tenant.id] = complaints.length;
        }
      } catch (err) {
        console.error(`Error fetching data for tenant ${tenant.id}:`, err);
      }
    }

    setLegalStatuses(legalStatusesData);
    setComplaintCounts(complaintCountsData);
  };

  const addAttribute = async () => {
    if (!newAttribute.trim()) return;
    setLoading(true);
    try {
      const payload = { attribute: newAttribute, type: newAttributeType };
      if (newAttributeType === 'relation') payload.relation_table = newAttributeRelationTable;
      const res = await fetch(`${API_BASE_URL}/api/tenants/attributes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!res.ok) throw new Error('Failed to add attribute');
      await fetchData();
      await fetchAttributeTypes('tenants');
      setShowAddTenantAttributeModal(false);
      setNewAttribute('');
      setNewAttributeType('string');
      setNewAttributeRelationTable('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const deleteAttribute = async () => {
    if (!attributeToDelete.trim()) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/tenants/attributes/${attributeToDelete}`, {
        method: 'DELETE'
      });
      if (!res.ok) throw new Error('Failed to delete attribute');
      await fetchData();
      await fetchAttributeTypes('tenants');
      setShowDeleteTenantAttributeModal(false);
      setAttributeToDelete('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const removeTenant = async (id) => {
    if (!window.confirm('Are you sure you want to delete this tenant?')) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/tenants/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Failed to delete tenant');
      await fetchData();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleTenantSort = (key) => {
    const dbKey = toDbField(key);
    if (tenantSortKey === dbKey) setTenantSortOrder(tenantSortOrder === 'asc' ? 'desc' : 'asc');
    else {
      setTenantSortKey(dbKey);
      setTenantSortOrder('asc');
    }
  };

  const sortedFilteredTenants = [...tenants]
    .filter(tenant => Object.values(tenant).some(value => value?.toString().toLowerCase().includes(tenantSearch.toLowerCase())))
    .sort((a, b) => {
      if (!tenantSortKey) return 0;
      
      let aValue, bValue;
      
      // Special handling for property_id sorting - sort by property name instead
      if (tenantSortKey === 'property_id') {
        aValue = getPropertyName(a.property_id || a['Property Id'] || a['Property']) || '';
        bValue = getPropertyName(b.property_id || b['Property Id'] || b['Property']) || '';
      } else {
        aValue = a[tenantSortKey] || '';
        bValue = b[tenantSortKey] || '';
      }
      
      return tenantSortOrder === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    });

  // Pagination logic
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const currentTenants = sortedFilteredTenants.slice(startIndex, endIndex);
  const totalPages = Math.ceil(sortedFilteredTenants.length / rowsPerPage);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleRowsPerPageChange = (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(1); // Reset to first page when changing rows per page
  };

  const handleEditTenant = (tenant) => {
    // Navigate to the dedicated edit page
    navigate(`/tenant/${tenant.id}`);
  };

  const saveTenant = async (updatedTenant) => {
    const required = ['First Name', 'Last Name', 'Unit Address'];
    const missing = required.filter(k => !updatedTenant[k]);
    if (missing.length > 0) return setError(`Missing: ${missing.join(', ')}`);
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/tenants/${updatedTenant.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedTenant)
      });
      if (!res.ok) throw new Error('Failed to update tenant');
      setEditTenant(null);
      setShowTenantModal(false);
      await fetchData();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const addTenant = async () => {
    const required = ['First Name', 'Last Name', 'Unit Address'];
    const missing = required.filter(k => !newTenant[k]);
    if (missing.length > 0) return setError(`Missing: ${missing.join(', ')}`);
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/tenants`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newTenant)
      });
      if (!res.ok) throw new Error('Failed to create tenant');
      setShowTenantModal(false);
      await fetchData();
      // Reset form
      const emptyNew = {};
      tenantAttributes.forEach(attr => {
        emptyNew[attr] = '';
      });
      emptyNew.property_id = '';
      setNewTenant(emptyNew);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCSVUpload = async () => {
    if (!uploadFile) {
      setError('Please select a file to upload');
      return;
    }

    setLoading(true);
    setUploadProgress('Uploading...');
    
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);

      // Add timeout to the fetch request
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 60000); // 60 second timeout

      const response = await fetch(`${API_BASE_URL}/api/tenants/upload-csv`, {
        method: 'POST',
        body: formData,
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      const result = await response.json();
      
      if (response.ok) {
        setUploadProgress(`Upload completed! ${result.success_count} records imported successfully.`);
        if (result.new_attributes_added && result.new_attributes_added.length > 0) {
          setUploadProgress(prev => prev + ` New attributes added: ${result.new_attributes_added.join(', ')}`);
        }
        if (result.error_count > 0) {
          setUploadProgress(prev => prev + ` ${result.error_count} errors occurred.`);
        }
        await fetchData();
        await fetchAttributeTypes('tenants');
        setTimeout(() => {
          setShowExcelUploadModal(false);
          setUploadFile(null);
          setUploadProgress(null);
        }, 3000);
      } else {
        setError(result.error || 'Upload failed');
      }
    } catch (err) {
      if (err.name === 'AbortError') {
        setError('Upload timed out. Please try with a smaller file or check your connection.');
      } else {
        setError('Upload failed: ' + err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  // Initialize hidden columns for attributes beyond the first 8
  React.useEffect(() => {
    if (tenantAttributes.length > 0 && Object.keys(hiddenColumns).length === 0) {
      const initialHiddenColumns = {};
      tenantAttributes.forEach((attr, index) => {
        if (index >= 8) { // Hide attributes beyond the first 8
          initialHiddenColumns[attr] = true;
        }
      });
      setHiddenColumns(initialHiddenColumns);
    }
  }, [tenantAttributes, hiddenColumns]);

  // Calculate visible attributes - show all attributes that aren't hidden
  const visibleTenantAttributes = tenantAttributes
    .filter(attr => !hiddenColumns[attr])
    .sort((a, b) => {
      // Put Property column first if it exists
      if (a === 'Property') return -1;
      if (b === 'Property') return 1;
      return 0;
    })
    .slice(0, MAX_VISIBLE_COLUMNS);

  return (
    <div className="notion-card">
      <div className="notion-header">
        <h1 className="notion-title">Tenants Database</h1>
        <div className="notion-actions">
          <input 
            type="text" 
            value={tenantSearch} 
            onChange={(e) => setTenantSearch(e.target.value)} 
            placeholder="🔍 Search Tenants" 
            className="search" 
          />
          <button onClick={() => handleTenantSort('Last Name')} className="notion-button">Sort by Name</button>
          <button onClick={() => { setEditTenant(null); setShowTenantModal(true); }} className="notion-button notion-button-primary" disabled={loading}>+ New Tenant</button>

          <button onClick={() => setShowAddTenantAttributeModal(true)} className="notion-button" disabled={loading}>+ Add Attribute</button>
          <button onClick={() => setShowDeleteTenantAttributeModal(true)} className="notion-button" disabled={loading}>- Delete Attribute</button>
          <button onClick={() => setShowExcelUploadModal(true)} className="notion-button" disabled={loading}>📊 Upload CSV</button>
          <button onClick={() => setShowColumnSettings(true)} className="notion-button">⚙️ Column Settings</button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}
      
      {loading && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '20px',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          margin: '10px 0',
          border: '1px solid #e9ecef'
        }}>
          <div style={{ marginRight: '10px' }}>🔄</div>
          <span>Loading tenants data...</span>
        </div>
      )}

      {showTenantModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2 className="notion-modal-title">
              {editTenant ? 'Edit Tenant' : 'Add New Tenant'}
            </h2>
            <form onSubmit={(e) => { 
              e.preventDefault(); 
              if (editTenant) saveTenant(editTenant); 
              else addTenant();
            }}>
              {tenantAttributes.map(attr => {
                const dbField = toDbField(attr);
                if (dbField === 'property' || attr === 'Property') {
                  return (
                    <div key={attr} className="notion-modal-field">
                      <label>{attr}</label>
                      <select
                        value={editTenant?.property_id || newTenant.property_id || ''}
                        onChange={(e) => {
                          const updated = editTenant ? { ...editTenant } : { ...newTenant };
                          updated.property_id = e.target.value ? parseInt(e.target.value) : null;
                          editTenant ? setEditTenant(updated) : setNewTenant(updated);
                        }}
                        className="notion-input"
                      >
                        <option value="">Select Property</option>
                        {properties.map(p => (
                          <option key={p.id} value={p.id}>
                            {p['Property Name'] || p.property_name || p.name || p.Property || `Property ${p.id}`}
                          </option>
                        ))}
                      </select>
                    </div>
                  );
                }
                // Relation field — render as dropdown
                const typeVal = attributeTypes[attr] || '';
                if (typeof typeVal === 'string' && typeVal.startsWith('relation:')) {
                  const relTable = typeVal.split(':')[1];
                  const options = relationOptions[relTable] || [];
                  if (options.length === 0 && relTable) fetchRelationOptions(relTable);
                  return (
                    <div key={attr} className="notion-modal-field">
                      <label>{attr}</label>
                      <select
                        value={editTenant ? editTenant[attr] || '' : newTenant[attr] || ''}
                        onChange={(e) => {
                          const updated = editTenant ? { ...editTenant } : { ...newTenant };
                          updated[attr] = e.target.value;
                          editTenant ? setEditTenant(updated) : setNewTenant(updated);
                        }}
                        className="notion-input"
                      >
                        <option value="">Select {attr}...</option>
                        {options.map(opt => <option key={opt.id} value={opt.name}>{opt.name}</option>)}
                      </select>
                    </div>
                  );
                }
                return (
                  <div key={attr} className="notion-modal-field">
                    <label>{attr}</label>
                    <input
                      type={isNumberField(dbField) ? 'number' : 'text'}
                      placeholder={attr}
                      value={editTenant ? editTenant[attr] || '' : newTenant[attr] || ''}
                      onChange={(e) => {
                        const updated = editTenant ? { ...editTenant } : { ...newTenant };
                        updated[attr] = e.target.value;
                        editTenant ? setEditTenant(updated) : setNewTenant(updated);
                      }}
                      required={['First Name', 'Last Name', 'Unit Address'].includes(attr)}
                      className="notion-input"
                    />
                  </div>
                );
              })}
              <div className="notion-modal-actions">
                <button type="submit" className="notion-button notion-button-primary">Save</button>
                <button type="button" className="notion-button" onClick={() => setShowTenantModal(false)}>Cancel</button>
                {editTenant && <button type="button" className="notion-button notion-button-danger" onClick={() => removeTenant(editTenant.id)}>Delete</button>}
              </div>
            </form>
          </div>
        </div>
      )}

      {showAddTenantAttributeModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2 className="notion-modal-title">Add New Tenant Attribute</h2>
            <div className="notion-modal-field">
              <label>Attribute Name</label>
              <input
                type="text"
                value={newAttribute}
                onChange={(e) => setNewAttribute(e.target.value)}
                placeholder="Attribute Name"
                className="notion-input"
              />
            </div>
            <div className="notion-modal-field">
              <label>Data Type</label>
              <select
                value={newAttributeType}
                onChange={(e) => { setNewAttributeType(e.target.value); if (e.target.value !== 'relation') setNewAttributeRelationTable(''); }}
                className="notion-input"
              >
                <option value="string">String</option>
                <option value="number">Number</option>
                <option value="currency">Currency</option>
                <option value="relation">Relation (Link to Database)</option>
              </select>
            </div>
            {newAttributeType === 'relation' && (
              <div className="notion-modal-field">
                <label>Link to Database</label>
                <select value={newAttributeRelationTable} onChange={e => setNewAttributeRelationTable(e.target.value)} className="notion-input">
                  <option value="">Select a database...</option>
                  {availableDatabases.filter(db => db.table_name !== 'tenants').map(db => (
                    <option key={db.table_name} value={db.table_name}>{db.icon} {db.display_name}</option>
                  ))}
                </select>
              </div>
            )}
            <div className="notion-modal-actions">
              <button className="notion-button notion-button-primary" onClick={addAttribute} disabled={loading || (newAttributeType === 'relation' && !newAttributeRelationTable)}>Add</button>
              <button className="notion-button" onClick={() => setShowAddTenantAttributeModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {showDeleteTenantAttributeModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2 className="notion-modal-title">Delete Tenant Attribute</h2>
            <div className="notion-modal-field">
              <label>Select Attribute</label>
              <select 
                className="notion-input" 
                value={attributeToDelete} 
                onChange={(e) => setAttributeToDelete(e.target.value)}
              >
                <option value="">Select an attribute</option>
                {tenantAttributes.map(attr => <option key={attr} value={attr}>{attr}</option>)}
              </select>
            </div>
            <div className="notion-modal-actions">
              <button className="notion-button notion-button-danger" onClick={deleteAttribute} disabled={loading || !attributeToDelete}>Delete</button>
              <button className="notion-button" onClick={() => setShowDeleteTenantAttributeModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {showExcelUploadModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2 className="notion-modal-title">Upload CSV File</h2>
            <div className="excel-upload-info">
              <p><strong>Instructions:</strong></p>
              <ul>
                <li>Upload a CSV file (.csv) with tenant data</li>
                <li>Column headers should match existing attributes or will be added as new attributes</li>
                <li>Required columns: First Name, Last Name, Unit Address</li>
                <li>Optional columns: Email, Phone, Social Security, Property (use property ID)</li>
                <li>Any additional columns will be added as new attributes</li>
                <li>Make sure your CSV uses commas as separators</li>
              </ul>
            </div>
            <div className="notion-modal-field">
              <label>Select CSV File</label>
              <input
                type="file"
                accept=".csv"
                onChange={(e) => setUploadFile(e.target.files[0])}
                className="notion-input"
              />
            </div>
            {uploadProgress && (
              <div className="upload-progress">
                {uploadProgress}
              </div>
            )}
            <div className="notion-modal-actions">
                              <button 
                  className="notion-button notion-button-primary" 
                  onClick={handleCSVUpload} 
                  disabled={loading || !uploadFile}
                >
                  {loading ? 'Uploading...' : 'Upload'}
                </button>
              <button 
                className="notion-button" 
                onClick={() => {
                  setShowExcelUploadModal(false);
                  setUploadFile(null);
                  setUploadProgress(null);
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showColumnSettings && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2 className="notion-modal-title">Column Settings</h2>
            <div className="column-settings">
              <h3>Visibility</h3>
              {tenantAttributes.map(attr => (
                <label key={attr} className="column-toggle">
                  <input
                    type="checkbox"
                    checked={!hiddenColumns[attr]}
                    onChange={() => toggleColumnVisibility(attr)}
                  />
                  {attr}
                </label>
              ))}
            </div>
            <div className="notion-modal-actions">
              <button className="notion-button" onClick={() => setShowColumnSettings(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      <div className="notion-table-container">
        <table className="notion-table">
          <thead>
            <tr>
              {visibleTenantAttributes.map(attr => (
                <th 
                  key={attr} 
                  className="notion-th" 
                  onClick={() => handleTenantSort(attr)}
                  style={{ width: getColumnWidth(attr), position: 'relative' }}
                >
                  {attr === 'Property Id' ? 'Property Name' : attr}
                  <div
                    className="resize-handle"
                    onMouseDown={(e) => handleMouseDown(e, attr)}
                  />
                </th>
              ))}
              <th className="notion-th">Legal Status</th>
              <th className="notion-th">Complaints</th>
              <th className="notion-th">Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentTenants.map(tenant => (
              <tr key={tenant.id} className="notion-row">
                {visibleTenantAttributes.map(attr => {
                  const dbField = toDbField(attr);
                  let value = tenant[attr] || tenant[dbField] || '';
                  
                  // Special handling for property field and property id field
                  if (dbField === 'property' || attr === 'Property' || attr === 'Property Id') {
                    value = getPropertyName(tenant.property_id || tenant['Property Id'] || tenant['Property']);
                  }
                  
                  // Format currency fields
                  if (isCurrencyField(dbField)) {
                    value = formatCurrency(value);
                  } else if (isNumberField(dbField) && value) {
                    value = parseFloat(value).toLocaleString();
                  }
                  
                  return <td key={attr} className="notion-td">{value}</td>;
                })}
                <td className="notion-td">
                  {(() => {
                    const statusDisplay = getLegalStatusDisplay(tenant.id);
                    return (
                      <div 
                        style={{
                          backgroundColor: statusDisplay.color,
                          color: 'white',
                          padding: '4px 8px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: '600',
                          textAlign: 'center',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}
                      >
                        {statusDisplay.label}
                      </div>
                    );
                  })()}
                </td>
                <td className="notion-td">
                  <div style={{ textAlign: 'center' }}>
                    {complaintCounts[tenant.id] || 0}
                  </div>
                </td>
                <td className="notion-td">
                  <button onClick={() => handleEditTenant(tenant)} className="notion-action">⋮</button>
                </td>
              </tr>
            ))}
            {currentTenants.length === 0 && !loading && (
              <tr>
                <td colSpan={visibleTenantAttributes.length + 3} className="notion-td" style={{ textAlign: 'center', padding: 0, border: 'none' }}>
                  <div className="db-empty-state" style={{ border: 'none', borderRadius: 0 }}>
                    <div className="db-empty-state-icon">👤</div>
                    <div className="db-empty-state-title">No tenants yet</div>
                    <div className="db-empty-state-text">Add your first tenant to start managing your rental relationships.</div>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>

        <div className="db-pagination">
          <div className="db-pagination-left">
            <label>Rows per page:</label>
            <select value={rowsPerPage} onChange={(e) => handleRowsPerPageChange(Number(e.target.value))}>
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </select>
          </div>
          <div className="db-pagination-center">
            Showing {sortedFilteredTenants.length === 0 ? 0 : startIndex + 1} to {Math.min(endIndex, sortedFilteredTenants.length)} of {sortedFilteredTenants.length} tenants
          </div>
          <div className="db-pagination-right">
            <button className="db-pagination-btn" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>Previous</button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) pageNum = i + 1;
              else if (currentPage <= 3) pageNum = i + 1;
              else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
              else pageNum = currentPage - 2 + i;
              return (
                <button key={pageNum} className={`db-pagination-btn ${currentPage === pageNum ? 'active' : ''}`} onClick={() => handlePageChange(pageNum)}>{pageNum}</button>
              );
            })}
            <button className="db-pagination-btn" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>Next</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TenantsCard;
